import type { VisualElement } from "@judo/model-api";
import { useValidationContext, useValidationContextOptional } from "../contexts";

/**
 * Result from useValidation hook.
 */
export interface ValidationResult {
	/** Error message or null */
	error: string | null;
	/** Whether field has been touched */
	isTouched: boolean;
	/** Mark field as touched */
	touch: () => void;
	/** Set error for this field */
	setError: (error: string | null) => void;
	/** Clear error for this field */
	clearError: () => void;
}

/**
 * Get field ID from visual element.
 * Uses the element's `name` property as the primary identifier since this matches
 * the `location` field returned by JUDO REST API validation errors.
 *
 * @param element - Visual element
 * @returns Field identifier (element name, or sourceId/xmi:id as fallback)
 */
function getFieldId(element: VisualElement): string {
	// Prefer element.name as it matches API validation error locations
	if (element.name) {
		return element.name;
	}
	// Fallback to sourceId or xmi:id
	const sourceId = (element as unknown as { sourceId?: string }).sourceId;
	return sourceId || element["xmi:id"];
}

/**
 * Hook for single element validation.
 *
 * @param element - Visual element
 * @returns ValidationResult
 */
export function useValidation(element: VisualElement): ValidationResult {
	const validation = useValidationContextOptional();
	const fieldId = getFieldId(element);

	if (!validation) {
		// Return stub when no validation context
		return {
			error: null,
			isTouched: false,
			touch: () => {},
			setError: () => {},
			clearError: () => {},
		};
	}

	const isTouched = validation.isTouched(fieldId);
	const error = isTouched ? validation.getError(fieldId) : null;

	return {
		error,
		isTouched,
		touch: () => validation.touch(fieldId),
		setError: (newError: string | null) => validation.setError(fieldId, newError),
		clearError: () => validation.clearError(fieldId),
	};
}

/**
 * Hook for form validation.
 * Requires ValidationProvider.
 *
 * @returns Full validation context
 * @throws Error if used outside ValidationProvider
 */
export function useFormValidation() {
	return useValidationContext();
}
